import React from 'react';
import { motion } from 'framer-motion';
import { ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PROGRAMS } from '../../lib/constants';
import { useLeadModal } from '../../lib/leadModalContext';

const Programs = () => {
  const { openLeadModal } = useLeadModal();
  return (
    <section id="programs" className="relative py-16 sm:py-20" data-testid="programs-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <span className="font-display uppercase tracking-[0.22em] text-[11px] text-[color:var(--neon-red)]">
              Programs
            </span>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl text-white tracking-tight">
              Find your class.
              <br />
              <span className="text-white/70">Beginner-friendly. Every age.</span>
            </h2>
          </div>
          <Button
            onClick={() => openLeadModal('programs-header')}
            className="bg-[color:var(--neon-red)] hover:bg-[#ff3a3a] text-white font-display uppercase tracking-[0.14em] rounded-xl px-5 py-5 glow-red-sm w-fit"
            data-testid="programs-book-trial-button"
          >
            Book Free Trial <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
          {PROGRAMS.map((p, i) => (
            <motion.div
              key={p.id}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.5, delay: 0.05 * i }}
              className="group panel rounded-2xl overflow-hidden light-trail hover:border-[color:var(--neon-red)]/45 hover:shadow-[0_0_0_1px_rgba(255,42,42,0.35),0_0_28px_rgba(255,42,42,0.18)] transition-all"
              data-testid={p.testid}
            >
              {/* Photo header */}
              {p.photo && (
                <div className="relative aspect-[16/9] overflow-hidden bg-[#121722]">
                  <img
                    src={p.photo}
                    alt={p.name}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.05]"
                    loading="lazy"
                  />
                  <div
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      background:
                        'linear-gradient(to top, rgba(7,8,10,0.85) 0%, rgba(7,8,10,0.35) 45%, rgba(7,8,10,0.10) 100%)',
                    }}
                  />
                  <div className="absolute left-4 top-4">
                    <span className="inline-block font-mono-ui text-[10px] uppercase tracking-[0.18em] text-[color:var(--neon-cyan)] bg-black/60 backdrop-blur-sm border border-white/15 rounded-full px-2 py-1">
                      {p.ages}
                    </span>
                  </div>
                  <div className="absolute left-5 right-5 bottom-3 flex items-end justify-between">
                    <h3 className="font-display text-2xl sm:text-3xl text-white tracking-tight leading-tight">
                      {p.name}
                    </h3>
                    <div className="h-9 w-9 shrink-0 rounded-full border border-white/15 grid place-items-center bg-black/40 backdrop-blur-sm group-hover:border-[color:var(--neon-red)]/70 transition-colors">
                      <ChevronRight className="h-4 w-4 text-white/85 group-hover:text-[color:var(--neon-red)]" />
                    </div>
                  </div>
                </div>
              )}

              {/* Body */}
              <div className="p-6">
                <p className="text-sm text-white/75 leading-relaxed">{p.summary}</p>
                <ul className="mt-4 space-y-1.5">
                  {p.bullets.map((b) => (
                    <li key={b} className="text-sm text-white/75 flex items-start gap-2">
                      <span className="h-1.5 w-1.5 mt-2 rounded-full bg-[color:var(--neon-red)]" />
                      {b}
                    </li>
                  ))}
                </ul>
                <div className="mt-5 pt-4 border-t border-white/10 flex items-center justify-between">
                  <span className="font-mono-ui text-[11px] uppercase tracking-[0.18em] text-white/50">
                    First class on us
                  </span>
                  <button
                    onClick={() => openLeadModal(`program-${p.id}`, { program: p.name })}
                    className="font-display uppercase tracking-[0.14em] text-[12px] text-[color:var(--neon-red)] hover:text-[#ff5a5a]"
                    data-testid={`program-cta-${p.id}`}
                  >
                    Try a class →
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Programs;
